import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { type ComponentPropsWithRef, type ReactNode } from 'react';

import { type MarginUtilityClass } from '@repo/ui/utils/spacing';

import s from './Separator.module.css';

export const separatorOrientationOptions = ['horizontal', 'vertical'] as const;
export type separatorOrientationOption =
  (typeof separatorOrientationOptions)[number];

export const separatorSizeOptions = ['sm', 'md', 'lg', 'xl'] as const;
export type SeparatorSize = (typeof separatorSizeOptions)[number];

export type SeparatorProps = ComponentPropsWithRef<'div'> & {
  asChild?: boolean;
  className?: string;
  children?: ReactNode;
  isDecorative?: boolean;
  margin?: MarginUtilityClass | MarginUtilityClass[];
  orientation?: separatorOrientationOption;
  size?: SeparatorSize;
};

export const separatorVariants = cva(s.separator, {
  variants: {
    orientation: {
      horizontal: s.horizontalOrientation,
      vertical: s.verticalOrientation,
    },
    size: {
      sm: s.smSize,
      md: s.mdSize,
      lg: s.lgSize,
      xl: s.xlSize,
    },
  },
});

export const Separator = (props: SeparatorProps) => {
  const {
    asChild = false,
    children,
    isDecorative = false,
    margin,
    orientation = 'horizontal',
    size = 'sm',
    className,
    ...attrs
  } = props;

  const ariaOrientation = orientation === 'vertical' ? orientation : undefined;
  const semanticProps = isDecorative
    ? { role: 'none' }
    : { 'aria-orientation': ariaOrientation, role: 'separator' };

  const Component = asChild ? Slot : 'div';

  return (
    <Component
      className={clsx(
        separatorVariants({
          orientation,
          size,
          className,
        }),
        margin,
      )}
      data-orientation={orientation}
      {...semanticProps}
      {...attrs}
    >
      {children}
    </Component>
  );
};
